import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { useSnackbar } from "notistack";
import { unparse } from "papaparse";
import { AssessmentDto, DoubleMaterialityIroTypeEnum } from "@green0meter/api-client";
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  GridLogicOperator,
  getGridSingleSelectOperators,
  GridFilterItem,
} from "@mui/x-data-grid";

import ESRSAreaChip from "../../../ESRSAreaChip/ESRSAreaChip";
import { ESRS_ENUM_TO_STRING } from "../../../Stakeholders/components/ESRS.constants";
import DeleteConfirmationDialog from "../../../DeleteConfirmationDialog/DeleteConfirmationDialog";
import {
  normalizeIro,
  IRO_CHIP_COLORS,
  IRO_LABEL_MAP,
} from "../../utils/IROUtils";

import {
  Stack,
  Box,
  Typography,
  Button,
  Switch,
  IconButton,
  Chip,
} from "@components/base";
import { useUserStore } from "@store/user";
import {
  useGetAssessments,
  useGetQuestions,
  useUpdateQuestion,
  useDeleteQuestion,
} from "@api";
import type { QuestionItemType } from "@api";
import { getYearFromSearchParams } from "@helpers";

const IRO_TRANSLATION_KEYS: Record<
  DoubleMaterialityIroTypeEnum,
  string
> = {
  [DoubleMaterialityIroTypeEnum.Impact]: "doubleMateriality.iro.impact",
  [DoubleMaterialityIroTypeEnum.Risk]: "doubleMateriality.iro.risk",
  [DoubleMaterialityIroTypeEnum.Opportunity]:
    "doubleMateriality.iro.opportunity",
};

const iroFilterOperators = getGridSingleSelectOperators()
  .filter((operator) => operator.value === "is" || operator.value === "not")
  .map((operator) => ({
    ...operator,
    getApplyFilterFn: (filterItem: GridFilterItem) => {
      if (!filterItem.field || !filterItem.value || !filterItem.operator) {
        return null;
      }

      return (value: DoubleMaterialityIroTypeEnum | DoubleMaterialityIroTypeEnum[]) => {
        const values = Array.isArray(value) ? value : [value];

        if (operator.value === "is") {
          return values.includes(filterItem.value);
        }
        if (operator.value === "not") {
          return !values.includes(filterItem.value);
        }

        return false;
      };
    },
  }));

interface IDoubleMaterialityQuestionsTable {
  onAddQuestion?: () => void;
  onEditQuestion?: (question: QuestionItemType) => void;
  onOpenAIAssistant?: () => void;
  onOpenRanges?: () => void;
}

const DoubleMaterialityQuestionsTable: FC<IDoubleMaterialityQuestionsTable> = ({
  onAddQuestion,
  onEditQuestion,
  onOpenAIAssistant,
  onOpenRanges,
}) => {
  const { i18n, t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();
  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);


  const { questions, isPendingGetQuestions } = useGetQuestions(user, {
    year,
    pageSize: 1000,
    pageNumber: 1,
  });
  const { assessments } = useGetAssessments(user, {
    year,
    pageSize: 1000,
    pageNumber: 1,
  });
  const { updateQuestion, isPendingUpdateQuestion } = useUpdateQuestion(user);
  const { deleteQuestionAsync, isPendingDeleteQuestion } = useDeleteQuestion(user);
  const currentLanguage = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase();
  const currentLanguageBase = currentLanguage.split("-")[0];
  const [questionToDelete, setQuestionToDelete] = useState<QuestionItemType | null>(
    null
  );
  const assessmentMap = useMemo(() => {
    const map = new Map<string, AssessmentDto>();

    assessments?.data.forEach((assessment) => {
      map.set(assessment.id, assessment);
    });

    return map;
  }, [assessments?.data]);

  const renderHTML = (html?: string): JSX.Element => (
    <Box
      sx={{
        maxWidth: 320,
        overflow: "hidden",
      }}
    >
      <Typography
        component="div"
        variant="body2"
        sx={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 5,
          overflow: "hidden",
          textOverflow: "ellipsis",
          "& p": { m: 0 },
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: html ?? "" }} />
      </Typography>
    </Box>
  );

  const getLocalizedTranslation = (value: unknown): string => {
    if (typeof value === "string") {
      return value;
    }

    if (!value || typeof value !== "object") {
      return "";
    }

    const translations = Object.entries(value as Record<string, unknown>).reduce(
      (acc, [key, translation]) => {
        if (typeof translation === "string" && translation.trim()) {
          acc[key.toLowerCase()] = translation;
        }

        return acc;
      },
      {} as Record<string, string>,
    );
    const fallbackOrder = [currentLanguage, currentLanguageBase, "en", "cs"];

    for (const languageKey of fallbackOrder) {
      const translation = translations[languageKey];

      if (translation) {
        return translation;
      }
    }

    return Object.values(translations)[0] ?? "";
  };

  const columns: GridColDef<QuestionItemType>[] = [
    {
      field: "enabled",
      headerName: t("common.enabled", "Enabled"),
      width: 90,
      renderCell: (params) => (
        <Switch
          checked={Boolean(params.value)}
          onChange={() => updateQuestion(params.row.id, { enabled: !params.value })}
          disabled={isPendingUpdateQuestion}
          size="small"
        />
      ),
      type: "boolean",
    },
    {
      field: "assessmentId",
      headerName: t("assessments.table.assessmentId", "Assessment ID"),
      width: 160,
      valueGetter: (value, row) => {
        const assessment = assessments?.data.find((a) => a.id === row.assessmentId);


        return assessment?.assessmentId ?? row.assessmentId ?? "-";
      },
    },
    {
      field: "iro",
      headerName: "IRO",
      width: 180,
      type: "singleSelect",
      valueOptions: [
        { value: DoubleMaterialityIroTypeEnum.Impact, label: IRO_LABEL_MAP[DoubleMaterialityIroTypeEnum.Impact] },
        { value: DoubleMaterialityIroTypeEnum.Risk, label: IRO_LABEL_MAP[DoubleMaterialityIroTypeEnum.Risk] },
        { value: DoubleMaterialityIroTypeEnum.Opportunity, label: IRO_LABEL_MAP[DoubleMaterialityIroTypeEnum.Opportunity] },
      ],
      filterOperators: iroFilterOperators,
      renderCell: (params) => {
        const values = normalizeIro(params.row.iro);

        return (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ my: 1 }}>
            {values.map((value, index) => {
              const colors = IRO_CHIP_COLORS[value];


              return (
                <Chip
                  key={`${value}-${index}`}
                  label={t(IRO_TRANSLATION_KEYS[value], IRO_LABEL_MAP[value])}
                  size="small"
                  variant="outlined"
                  sx={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    borderWidth: 1,
                    borderStyle: "solid",
                    color: colors.text,
                    fontWeight: 500,
                  }}
                />
              );
            })}
          </Stack>
        );
      },
      valueGetter: (_value, row) => normalizeIro(row.iro),
      sortComparator: (valueA, valueB) => {
        const labelsA = (valueA as DoubleMaterialityIroTypeEnum[]).map((v) => IRO_LABEL_MAP[v]).sort().join(", ");
        const labelsB = (valueB as DoubleMaterialityIroTypeEnum[]).map((v) => IRO_LABEL_MAP[v]).sort().join(", ");

        return labelsA.localeCompare(labelsB);
      },
    },
    {
      field: "topic",
      headerName: "Topic",
      width: 120,
      type: "singleSelect",
      valueOptions: [
        { value: "E1", label: "E1: Climate Change" },
        { value: "E2", label: "E2: Pollution" },
        { value: "E3", label: "E3: Water & Marine" },
        { value: "E4", label: "E4: Biodiversity" },
        { value: "E5", label: "E5: Circular Economy" },
        { value: "S1", label: "S1: Own Workforce" },
        { value: "S2", label: "S2: Workers in Value Chain" },
        { value: "S3", label: "S3: Affected Communities" },
        { value: "S4", label: "S4: Consumers & End Users" },
        { value: "G1", label: "G1: Business Conduct" },
        { value: "OTHER", label: "Other" },
      ],
      valueGetter: (_value, row) => {
        const assessment = assessments?.data.find((a) => a.id === row.assessmentId);

        if (!assessment || assessment.topic === null || assessment.topic === undefined) {
          return null;
        }

        return ESRS_ENUM_TO_STRING[assessment.topic] ?? `ESRS ${assessment.topic}`;
      },
      renderCell: (params) => {
        if (!params.value) return "-";

        return <ESRSAreaChip label={params.value} uppercase />;
      },
    },
    {
      field: "sustainabilityMatter",
      headerName: "Sustainability Matter",
      width: 200,
      valueGetter: (value, row) => {
        const assessment = assessments?.data.find((a) => a.id === row.assessmentId);


        return assessment?.sustainabilityMatter ?? "-";
      }
    },
    {
      field: "stakeholderType",
      headerName: t("stakeholders.table.stakeholderType", "Stakeholder type"),
      width: 160,
    },
    {
      field: "questions",
      headerName: t("doubleMateriality.questions.table.question", "Question"),
      width: 300,
      renderCell: (params): JSX.Element => {
        const value = getLocalizedTranslation(params.row.questions);


        return value ? renderHTML(value) : (
          <Typography variant="body2" color="text.secondary">
            {t("doubleMateriality.questions.noTranslation", {
              defaultValue: `No translation for {{lng}}`,
              lng: currentLanguageBase,
            })}
          </Typography>
        );
      },
      valueGetter: (_value, row) => getLocalizedTranslation(row.questions),
    },
    {
      field: "descriptions",
      headerName: t("doubleMateriality.questions.table.description", "Description"),
      width: 300,
      renderCell: (params): JSX.Element => {
        const value = getLocalizedTranslation(params.row.descriptions);


        return value ? renderHTML(value) : (
          <Typography variant="body2" color="text.secondary">
            {t("doubleMateriality.questions.noTranslation", {
              defaultValue: `No translation for {{lng}}`,
              lng: currentLanguageBase,
            })}
          </Typography>
        );
      },
      valueGetter: (_value, row) => getLocalizedTranslation(row.descriptions),
    },
    {
      field: "activateTextField",
      headerName: t("doubleMateriality.questions.table.activateTextField", "Activate text field"),
      width: 180,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" align="center">
          {params.value != null ? String(params.value) : "-"}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: t("stakeholders.table.actions", "Action"),
      width: 120,
      align: "center",
      headerAlign: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1} justifyContent="center" width="100%">
          <IconButton
            onClick={() => onEditQuestion?.(params.row)}
            color="primary"
            size="small"
          >
            <FeatherIcon icon="edit-3" size={16} />
          </IconButton>
          <IconButton
            onClick={() => setQuestionToDelete(params.row)}
            color="error"
            size="small"
            disabled={isPendingDeleteQuestion}
          >
            <FeatherIcon icon="trash-2" size={16} />
          </IconButton>
        </Stack>
      ),
    },
  ];

  const handleExportCSV = (): void => {
    const data = questions?.data ?? [];

    if (!data.length) {
      enqueueSnackbar(t("assessments.export.noData", "No data to export"), {
        variant: "warning",
      });

      return;
    }

    const csvData = data.map((q) => ({
      Enabled: q.enabled,
      "Assessment ID": q.assessmentId,
      IRO: normalizeIro(q.iro)
        .map((value) => IRO_LABEL_MAP[value])
        .join(", "),
      Topic: (() => {
        const assessment = assessmentMap.get(q.assessmentId);

        if (!assessment || assessment.topic === null || assessment.topic === undefined) {
          return "";
        }

        return ESRS_ENUM_TO_STRING[assessment.topic] ?? `ESRS ${assessment.topic}`;
      })(),
      "Sustainability Matter": assessmentMap.get(q.assessmentId)
        ?.sustainabilityMatter,
      "Stakeholder Type": q.stakeholderType,
      Question:
        getLocalizedTranslation(q.questions) ||
        `No translation for ${currentLanguageBase}`,
      Description:
        getLocalizedTranslation(q.descriptions) ||
        `No translation for ${currentLanguageBase}`,
      "Activate Text Field": q.activateTextField,
    }));

    const csv = unparse(csvData);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `questions-${new Date().toISOString().split("T")[0]}.csv`,
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      enqueueSnackbar(t("assessments.export.success", "Export successful"), {
        variant: "success",
      });
    }
  };

  if (isPendingGetQuestions) {
    return <div>{t("common.loading")}</div>;
  }


  // Custom Toolbar
  const CustomToolbar = (): JSX.Element => {
    return (
      <GridToolbarContainer
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <GridToolbarQuickFilter />
          <GridToolbarFilterButton />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            startIcon={<FeatherIcon icon="zap" size={16} />}
            onClick={onOpenAIAssistant}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("doubleMateriality.questions.aiAssistant", "AI Assistant")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="sliders" size={16} />}
            onClick={onOpenRanges}
            disabled={!onOpenRanges}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("doubleMateriality.questions.editRanges", "Double materiality ranges")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="plus" size={16} />}
            onClick={onAddQuestion}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("doubleMateriality.questions.addQuestion", "ADD QUESTION")}
          </Button>

          <Button
            variant="text"
            startIcon={<FeatherIcon icon="download" size={16} />}
            onClick={handleExportCSV}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("assessments.export.title", "Export")}
          </Button>
        </Stack>
      </GridToolbarContainer>
    );
  };

  return (
    <Box>
      <DataGrid
        rows={questions?.data ?? []}
        columns={columns}
        loading={isPendingGetQuestions}
        hideFooterPagination
        initialState={{
          filter: {
            filterModel: {
              items: [],
              logicOperator: GridLogicOperator.Or,
            },
          },
        }}
        slots={{
          toolbar: CustomToolbar,
          noRowsOverlay: () => (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", p: 2 }}>
              <Typography>{t("common.noData", "No data available")}</Typography>
            </Box>
          )
        }}
        // this has to be defined inline to work properly, not sure why
        slotProps={{
          panel: {
            sx: {
              "& .MuiDataGrid-filterForm": {
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                alignItems: "flex-end",
                gap: 2,
                p: 2,
              },
              "& .MuiDataGrid-filterFormDeleteIcon": {
                order: -1,
                marginBottom: 0.5,
              },
              "& .MuiDataGrid-filterFormColumnInput": {
                minWidth: 150,
              },
              "& .MuiDataGrid-filterFormOperatorInput": {
                minWidth: 120,
              },
              "& .MuiDataGrid-filterFormValueInput": {
                minWidth: 220,
                "& label": {
                  visibility: "hidden",
                },
                "& input.MuiInputBase-input": {
                  paddingBottom: "24px",
                },
              },
            },
          },
        }}
        getRowHeight={() => 'auto'}
        sx={{
          border: 0,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 1
          },
          "& .MuiDataGrid-columnHeader": {
            backgroundColor: "#f5f5f5",
          },
        }}
        disableRowSelectionOnClick
      />
      <DeleteConfirmationDialog
        open={Boolean(questionToDelete)}
        onClose={() => setQuestionToDelete(null)}
        onConfirm={async () => {
          if (questionToDelete) {
            await deleteQuestionAsync(questionToDelete.id);
            setQuestionToDelete(null);
          }
        }}
        title={t("doubleMateriality.questions.delete.title", "Delete question")}
        description={t(
          "doubleMateriality.questions.delete.description",
          "Are you sure you want to delete this question? This action cannot be undone."
        )}
        isLoading={isPendingDeleteQuestion}
      />
    </Box>
  );
};

export default DoubleMaterialityQuestionsTable;
